import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import loadImage from "blueimp-load-image";
import { db } from "./firebase";
import { doc, updateDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { initializeApp } from "firebase/app";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

// Initialize storage with explicit bucket
const firebaseConfig = {
  apiKey: "AIzaSyDWRy71b7QrAU8owggWNa1mQpqtjiiJBDQ",
  authDomain: "agentmiatest.firebaseapp.com",
  projectId: "agentmiatest",
  storageBucket: "agentmiatest.firebasestorage.app",
  messagingSenderId: "97168887618",
  appId: "1:97168887618:web:2a3da7390911df0fab82db"
};

const app = initializeApp(firebaseConfig);
const storage = getStorage(app);

const ReportEditor = ({ report, onSave, onCancel }) => {
  const [reportTitle, setReportTitle] = useState(report?.title || "");
  const [projectManager, setProjectManager] = useState(report?.createdBy || "");
  const [reportDate, setReportDate] = useState(report?.reportDate || "");
  const [clientName, setClientName] = useState(report?.clientName || "");
  const [projectAddress, setProjectAddress] = useState(report?.projectAddress || "");
  const [companyName, setCompanyName] = useState(report?.companyName || "");
  const [sections, setSections] = useState(report?.sections || [{ name: "Section 1", images: [] }]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  const isEditing = Boolean(report?.id);

  useEffect(() => {
    // Convert Firebase URLs back to displayable format for editing
    if (report?.sections) {
      const convertedSections = report.sections.map(section => ({
        ...section,
        images: section.images.map(img => ({
          ...img,
          url: img.imageUrl, // Use the Firebase URL for display
          file: null, // No file object for existing images
          isExisting: true // Flag to identify existing images
        }))
      }));
      setSections(convertedSections);
    }
  }, [report]);

  const showStatus = (message, type = "info") => {
    setStatusMessage({ text: message, type });
    console.log(message);
  };

  const clearStatus = () => {
    setStatusMessage("");
  };

  const markAsChanged = () => {
    setHasChanges(true);
  };

  const handleImageChange = (e, sectionIndex) => {
    const files = Array.from(e.target.files);
    const updatedSections = [...sections];
    
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        updatedSections[sectionIndex].images.push({
          file,
          url: event.target.result,
          caption: "",
          isExisting: false
        });
        setSections([...updatedSections]);
        markAsChanged();
      };
      reader.readAsDataURL(file);
    });
  };

  const handleReplaceImage = (sectionIndex, imageIndex, file) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const updatedSections = [...sections];
      updatedSections[sectionIndex].images[imageIndex] = {
        file,
        url: event.target.result,
        caption: updatedSections[sectionIndex].images[imageIndex].caption,
        isExisting: false
      };
      setSections(updatedSections);
      markAsChanged();
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (sectionIndex, imageIndex) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].images.splice(imageIndex, 1);
    setSections(updatedSections);
    markAsChanged();
  };

  const handleCaptionChange = (sectionIndex, imageIndex, caption) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].images[imageIndex].caption = caption;
    setSections(updatedSections);
    markAsChanged();
  };

  const handleSectionNameChange = (index, name) => {
    const updatedSections = [...sections];
    updatedSections[index].name = name;
    setSections(updatedSections);
    markAsChanged();
  };

  const addSection = () => {
    setSections([...sections, { name: `Section ${sections.length + 1}`, images: [] }]);
    markAsChanged();
  };

  const removeSection = (sectionIndex) => {
    if (sections.length <= 1) {
      showStatus("❌ Cannot remove the last section", "error");
      return;
    }
    
    if (window.confirm("Are you sure you want to remove this section and all its images?")) {
      const updatedSections = sections.filter((_, index) => index !== sectionIndex);
      setSections(updatedSections);
      markAsChanged();
    }
  };

  const saveReport = async () => {
    if (!reportTitle.trim()) {
      showStatus("❌ Project Title is required!", "error");
      return;
    }

    setIsSaving(true);
    clearStatus();
    
    try {
      showStatus("🔄 Starting save process...", "info");
      
      // Process sections and upload new images
      const sectionUploads = await Promise.all(
        sections.map(async (section, sectionIndex) => {
          showStatus(`🔄 Processing section ${sectionIndex + 1}: ${section.name}`, "info");
          
          const uploadedImages = await Promise.all(
            section.images.map(async (img, imgIndex) => {
              // If it's an existing image, keep the existing URL
              if (img.isExisting && img.imageUrl) {
                return {
                  caption: img.caption,
                  imageUrl: img.imageUrl,
                };
              }
              
              // If it's a new image, upload it
              if (img.file) {
                showStatus(`🔄 Uploading image ${imgIndex + 1} in section ${sectionIndex + 1}`, "info");
                
                try {
                  const timestamp = Date.now();
                  const fileName = `image-${sectionIndex}-${imgIndex}-${timestamp}`;
                  const storageRef = ref(storage, `photoReports/${section.name}/${fileName}`);
                  
                  await uploadBytes(storageRef, img.file);
                  const downloadURL = await getDownloadURL(storageRef);
                  
                  showStatus(`✅ Image ${imgIndex + 1} uploaded successfully`, "success");
                  
                  return {
                    caption: img.caption,
                    imageUrl: downloadURL,
                  };
                } catch (error) {
                  console.error(`Error uploading image ${imgIndex + 1}:`, error);
                  showStatus(`❌ Failed to upload image ${imgIndex + 1}: ${error.message}`, "error");
                  throw error;
                }
              }
              
              // Fallback for images with URL but no file
              return {
                caption: img.caption,
                imageUrl: img.url,
              };
            })
          );
          
          return {
            name: section.name,
            images: uploadedImages,
          };
        })
      );

      showStatus("🔄 Saving to Firestore...", "info");
      
      const reportData = {
        title: reportTitle,
        createdBy: projectManager,
        reportDate: reportDate,
        sections: sectionUploads,
        clientName,
        projectAddress,
        companyName,
      };

      let savedReport;
      
      if (isEditing) {
        // Update existing report
        reportData.updatedAt = serverTimestamp();
        await updateDoc(doc(db, "reports", report.id), reportData);
        savedReport = { ...reportData, id: report.id };
        showStatus(`✅ Report updated successfully!`, "success");
      } else {
        // Create new report
        reportData.dateCreated = serverTimestamp();
        const docRef = await addDoc(collection(db, "reports"), reportData);
        savedReport = { ...reportData, id: docRef.id };
        showStatus(`✅ Report saved successfully! ID: ${docRef.id}`, "success");
      }
      
      setHasChanges(false);
      
      // Call the onSave callback with the saved report
      if (onSave) {
        onSave(savedReport);
      }
      
    } catch (error) {
      console.error("Error saving report:", error);
      showStatus(`❌ Failed to save report: ${error.message}`, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const loadLogo = async () => {
    return new Promise((resolve) => {
      const logo = new Image();
      
      const logoPaths = [
        "/GEP-Logo-24-resized.jpg",
        "./GEP-Logo-24-resized.jpg",
        `${process.env.PUBLIC_URL || ""}/GEP-Logo-24-resized.jpg`
      ];
      
      let currentPathIndex = 0;
      
      const tryNextPath = () => {
        if (currentPathIndex >= logoPaths.length) {
          console.warn("⚠️ Logo not found, continuing without logo");
          showStatus("⚠️ Logo not found, continuing without logo", "warning");
          resolve(null);
          return;
        }
        
        logo.src = logoPaths[currentPathIndex];
        currentPathIndex++;
      };
      
      logo.onload = () => {
        console.log("✅ Logo loaded successfully");
        resolve(logo);
      };
      
      logo.onerror = () => {
        console.warn(`⚠️ Logo loading failed, trying next path...`);
        tryNextPath();
      };
      
      tryNextPath();
      
      setTimeout(() => {
        if (!logo.complete) {
          console.warn("⚠️ Logo loading timeout, continuing without logo");
          showStatus("⚠️ Logo loading timeout, continuing without logo", "warning");
          resolve(null);
        }
      }, 5000);
    });
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    clearStatus();
    
    try {
      showStatus("🔄 Generating PDF...", "info");
      
      const pdf = new jsPDF();
      const logo = await loadLogo();
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const maxImgWidth = 160;
      const maxImgHeight = 100;

      // Add logo to title page if available
      if (logo) {
        const logoWidth = 60;
        const logoAspectRatio = logo.width / logo.height;
        const logoHeight = logoWidth / logoAspectRatio;
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = (pageHeight - logoHeight) / 2 - 30;
        pdf.addImage(logo, "JPEG", logoX, logoY, logoWidth, logoHeight);
      }

      // Title page content
      pdf.setFont("Helvetica", "bold");
      pdf.setFontSize(20);
      pdf.text(`Project Title: ${reportTitle}`, 20, pageHeight - 60);

      pdf.setFontSize(12);
      const fields = [
        `Project Manager: ${projectManager}`,
        `Date of Report: ${reportDate}`,
        `Client Name: ${clientName}`,
        `Project Address: ${projectAddress}`,
        `Prepared By: ${companyName}`,
      ];
      fields.forEach((text, i) => {
        pdf.text(text, 20, pageHeight - 50 + i * 8);
      });

      // Process sections
      for (const section of sections) {
        pdf.addPage();
        
        if (logo) {
          const logoAspectRatio = logo.width / logo.height;
          pdf.addImage(logo, "JPEG", 10, 10, 30, 30 / logoAspectRatio);
        }
        
        pdf.setFontSize(16);
        pdf.setFont("Helvetica", "bold");
        pdf.text(section.name, pageWidth / 2, 30, { align: "center" });

        let yOffset = 50;
        let imageCounter = 0;

        for (let i = 0; i < section.images.length; i++) {
          if (imageCounter === 2) {
            pdf.addPage();
            yOffset = 50;
            imageCounter = 0;
            
            if (logo) {
              const logoAspectRatio = logo.width / logo.height;
              pdf.addImage(logo, "JPEG", 10, 10, 30, 30 / logoAspectRatio);
            }
          }

          pdf.setFontSize(12);
          pdf.text(section.images[i].caption || " ", pageWidth / 2, yOffset, { align: "center" });

          let imageSource = null;

          if (section.images[i].file) {
            // New image with file object
            imageSource = section.images[i].file;
          } else if (section.images[i].imageUrl) {
            // Existing image with imageUrl
            imageSource = section.images[i].imageUrl;
          }

          if (imageSource) {
            try {
              const loadResult = await loadImage(imageSource, {
                canvas: true,
                orientation: true,
                crossOrigin: 'Anonymous',
              });
              const canvas = loadResult.image;
              const imgData = canvas.toDataURL("image/jpeg");

              const aspectRatio = canvas.width / canvas.height;
              let imgWidth = maxImgWidth;
              let imgHeight = imgWidth / aspectRatio;
              if (imgHeight > maxImgHeight) {
                imgHeight = maxImgHeight;
                imgWidth = imgHeight * aspectRatio;
              }
              const centeredX = (pageWidth - imgWidth) / 2;

              pdf.addImage(imgData, "JPEG", centeredX, yOffset + 5, imgWidth, imgHeight);
              yOffset += imgHeight + 20;
              imageCounter++;
            } catch (error) {
              console.error(`Error loading image for PDF: ${error.message}`, imageSource);
              showStatus(`❌ Failed to load image for PDF: ${section.images[i].caption || 'Image'}`, "error");
            }
          } else {
            console.warn(`No valid image source found for section ${section.name}, image ${i}. Skipping.`);
          }
        }
      }

      showStatus("✅ PDF generated successfully", "success");
      pdf.save(`${reportTitle || "Photo_Report"}.pdf`);
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      showStatus(`❌ Failed to generate PDF: ${error.message}`, "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm("You have unsaved changes. Are you sure you want to cancel?")) {
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  const getStatusStyle = (type) => {
    const baseStyle = {
      padding: "10px",
      marginBottom: "20px",
      borderRadius: "5px",
      fontWeight: "bold"
    };
    
    switch (type) {
      case "success":
        return { ...baseStyle, backgroundColor: "#d4edda", color: "#155724", border: "1px solid #c3e6cb" };
      case "error":
        return { ...baseStyle, backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb" };
      case "warning":
        return { ...baseStyle, backgroundColor: "#fff3cd", color: "#856404", border: "1px solid #ffeaa7" };
      default:
        return { ...baseStyle, backgroundColor: "#d1ecf1", color: "#0c5460", border: "1px solid #bee5eb" };
    }
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>
      {/* Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "20px",
        flexWrap: "wrap",
        gap: "15px"
      }}>
        <h2 style={{ margin: 0 }}>
          {isEditing ? "✏️ Edit Report" : "📝 Create New Report"}
          {hasChanges && <span style={{ color: "#ffc107" }}> *</span>}
        </h2>
        <button 
          onClick={handleCancel}
          style={{
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            padding: "10px 15px",
            borderRadius: "5px",
            cursor: "pointer"
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div style={getStatusStyle(statusMessage.type)}>
          {statusMessage.text}
        </div>
      )}

      {/* Form fields */}
      <div style={{ 
        marginBottom: "20px", 
        border: "1px solid #ccc", 
        padding: "20px", 
        borderRadius: "5px",
        backgroundColor: "#f9f9f9"
      }}>
        <h3>Report Title Page</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <input 
            type="text" 
            placeholder="Project Title *" 
            value={reportTitle} 
            onChange={(e) => { setReportTitle(e.target.value); markAsChanged(); }}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
            required
          />
          <input 
            type="text" 
            placeholder="Project Manager" 
            value={projectManager} 
            onChange={(e) => { setProjectManager(e.target.value); markAsChanged(); }}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
          <input 
            type="text" 
            placeholder="Date of Report" 
            value={reportDate} 
            onChange={(e) => { setReportDate(e.target.value); markAsChanged(); }}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
          <input 
            type="text" 
            placeholder="Client Name" 
            value={clientName} 
            onChange={(e) => { setClientName(e.target.value); markAsChanged(); }}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
          <input 
            type="text" 
            placeholder="Project Address" 
            value={projectAddress} 
            onChange={(e) => { setProjectAddress(e.target.value); markAsChanged(); }}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
          <input 
            type="text" 
            placeholder="Prepared By" 
            value={companyName} 
            onChange={(e) => { setCompanyName(e.target.value); markAsChanged(); }}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
        </div>
      </div>

      {/* Sections */}
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} style={{ 
          border: "1px solid #ccc", 
          marginBottom: "30px", 
          padding: "20px",
          borderRadius: "5px",
          backgroundColor: "#f9f9f9"
        }}>
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            marginBottom: "15px" 
          }}>
            <h3 style={{ margin: 0 }}>{section.name}</h3>
            {sections.length > 1 && (
              <button
                onClick={() => removeSection(sectionIndex)}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "5px 10px",
                  borderRadius: "3px",
                  fontSize: "12px",
                  cursor: "pointer"
                }}
              >
                🗑️ Remove Section
              </button>
            )}
          </div>
          
          <input 
            type="text" 
            value={section.name} 
            onChange={(e) => handleSectionNameChange(sectionIndex, e.target.value)}
            style={{ 
              width: "100%", 
              padding: "8px", 
              marginBottom: "15px",
              border: "1px solid #ddd", 
              borderRadius: "3px",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          />
          
          {section.images.map((img, imgIndex) => (
            <div key={imgIndex} style={{ 
              marginBottom: "20px", 
              padding: "15px", 
              border: "1px solid #ddd",
              borderRadius: "5px",
              backgroundColor: "white"
            }}>
              <input 
                type="text" 
                placeholder="Picture Description" 
                value={img.caption} 
                onChange={(e) => handleCaptionChange(sectionIndex, imgIndex, e.target.value)}
                style={{ 
                  width: "100%", 
                  padding: "8px", 
                  marginBottom: "10px",
                  border: "1px solid #ddd", 
                  borderRadius: "3px"
                }}
              />
              <div style={{ marginBottom: "10px" }}>
                <button 
                  onClick={() => document.getElementById(`replace-${sectionIndex}-${imgIndex}`).click()}
                  style={{
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "8px 15px",
                    borderRadius: "3px",
                    marginRight: "10px",
                    cursor: "pointer"
                  }}
                >
                  Replace Image
                </button>
                <input 
                  type="file" 
                  id={`replace-${sectionIndex}-${imgIndex}`} 
                  accept="image/*" 
                  style={{ display: "none" }} 
                  onChange={(e) => handleReplaceImage(sectionIndex, imgIndex, e.target.files[0])} 
                />
                <button 
                  onClick={() => handleRemoveImage(sectionIndex, imgIndex)}
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "8px 15px",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }}
                >
                  Remove Image
                </button>
                {img.isExisting && (
                  <span style={{ 
                    marginLeft: "10px", 
                    fontSize: "12px", 
                    color: "#666",
                    fontStyle: "italic" 
                  }}>
                    (Existing image)
                  </span>
                )}
              </div>
              <img 
                src={img.url} 
                alt="preview" 
                style={{ 
                  maxWidth: "400px", 
                  maxHeight: "300px",
                  border: "1px solid #ddd",
                  borderRadius: "3px"
                }} 
              />
            </div>
          ))}
          
          <div style={{ marginTop: "15px" }}>
            <input 
              type="file" 
              accept="image/*" 
              multiple 
              onChange={(e) => handleImageChange(e, sectionIndex)}
              style={{ marginBottom: "10px" }}
            />
          </div>
        </div>
      ))}

      {/* Action Buttons */}
      <div style={{ marginTop: "30px", display: "flex", gap: "15px", flexWrap: "wrap" }}>
        <button 
          onClick={addSection}
          style={{
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: "5px",
            fontSize: "16px",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          ➕ Add Section
        </button>
        
        <button 
          onClick={generatePDF}
          disabled={isGenerating}
          style={{
            backgroundColor: isGenerating ? "#6c757d" : "#17a2b8",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: "5px",
            fontSize: "16px",
            cursor: isGenerating ? "not-allowed" : "pointer",
            fontWeight: "bold"
          }}
        >
          {isGenerating ? "Generating..." : "📄 Generate PDF"}
        </button>
        
        <button 
          onClick={saveReport}
          disabled={isSaving || !reportTitle.trim()}
          style={{
            backgroundColor: isSaving ? "#6c757d" : (!reportTitle.trim() ? "#6c757d" : "#ffc107"),
            color: isSaving || !reportTitle.trim() ? "#fff" : "#212529",
            border: "none",
            padding: "12px 20px",
            borderRadius: "5px",
            fontSize: "16px",
            cursor: (isSaving || !reportTitle.trim()) ? "not-allowed" : "pointer",
            fontWeight: "bold"
          }}
        >
          {isSaving ? "Saving..." : (isEditing ? "💾 Update Report" : "💾 Save Report")}
        </button>
      </div>
      
      {!reportTitle.trim() && (
        <p style={{ color: "#dc3545", marginTop: "10px", fontStyle: "italic" }}>
          * Project Title is required to save the report
        </p>
      )}
      
      {hasChanges && (
        <p style={{ color: "#ffc107", marginTop: "10px", fontStyle: "italic" }}>
          * You have unsaved changes
        </p>
      )}
    </div>
  );
};

export default ReportEditor;

